import React, { useState } from 'react';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import Icon from 'components/Utils/NewIcon';
import UIUtils from 'services/UIUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import PharmacyBadge from 'components/Pharmacy/PharmacyBadge';
import PharmacyFaxEditor from 'components/Pharmacy/PharmacyFaxEditor';
import SelectableListItem from 'components/Utils/SelectableListItem';
import './style.scss';
import ProgressMessage from 'components/Utils/ProgressMessage/index';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';

const PharmacySearch = ({ onSelect, initialPharmacies, showMap, defaultPharmacyId }) => {
  const dispatch = useAppDispatch();

  const pharmacies = useAppSelector((state) => state.pharmacies.pharmacies);
  const role = useAppSelector((state) => state.authentication.role);

  const [typingTimeout, setTypingTimeout] = useState();
  const [searchTerm, setSearchTerm] = useState();
  const [clickedItem, setClickedItem] = useState();
  const [searching, setSearching] = useState(false);

  function enterPressed(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      clearTimeout(typingTimeout);
      let value = searchTerm;
      if (value !== undefined && value !== '') {
        getPharmaciesWrapper(value);
      }
    }
  }

  function handleSearchChange(event) {
    clearTimeout(typingTimeout);
    let value = event.target.value;

    setSearchTerm(value);
    setTypingTimeout(
      setTimeout(() => {
        if (value !== undefined && value !== '') getPharmaciesWrapper(value);
      }, UIUtils.SEARCH_TYPE_TIMEOUT),
    );
  }

  async function getPharmaciesWrapper(value) {
    setSearching(true);
    await dispatch(api.Pharmacies.getPharmacies({ name: value, limit: 5 }));
    setSearching(false);
  }

  function selectItem(item) {
    setClickedItem(item);
    if (onSelect !== undefined) onSelect(item);
  }

  // With a search term, show the live results from the directory; with no input,
  // show only the patient's previously-used pharmacies (never fall back to the
  // last search results, which would leak stale rows into the empty state).
  let chosenPharmacies = pharmacies;
  if (!chosenPharmacies.map) chosenPharmacies = [];
  if (!searchTerm) chosenPharmacies = initialPharmacies || [];

  // Editing the preferred fax hits a physician/admin-only endpoint — don't offer
  // it to patients (they'd just get a 403).
  const canEditFax = role !== 'patient';

  return (
    <>
      <div className="search-column full-height-panel scroll-list">
        <div className="search-field">
          {role === 'patient' ? (
            <DefaultPatientTextField
              name="searchTerm"
              placeholder="Type the name here"
              label="Pharmacy name"
              value={searchTerm || ''}
              onKeyDown={enterPressed}
              onChange={handleSearchChange}
            />
          ) : (
            <>
              {!searching && <Icon src="magnifying-glass" />}
              <ProgressMessage size={20} show={!!searching} />
              <DefaultTextField
                name="searchTerm"
                autoFocus={true}
                label="Search by name"
                value={searchTerm || ''}
                onKeyDown={enterPressed}
                onChange={handleSearchChange}
              />
            </>
          )}
        </div>
        <div className="list" style={{ overflowY: 'auto' }}>
          {chosenPharmacies.length === 0 ? (
            <div className="pharmacy-search-empty">
              <Icon src="magnifying-glass" width="18px" />
              <span>
                {searchTerm
                  ? 'No pharmacies found. Try a different name.'
                  : 'Search for a pharmacy by name to get started.'}
              </span>
            </div>
          ) : (
            <List>
              {chosenPharmacies.map((pharmacy) => {
                let selected = clickedItem !== undefined && clickedItem.name === pharmacy.name;
                let isDefault = !!defaultPharmacyId && pharmacy.id === defaultPharmacyId;
                return (
                  <SelectableListItem
                    key={pharmacy.name}
                    selected={selected}
                    style={{ padding: 0 }}
                    onClick={() => selectItem(pharmacy)}
                    item={
                      <div className="pharmacy-search-row">
                        <PharmacyBadge
                          pharmacy={pharmacy}
                          showMap={showMap}
                          isDefault={isDefault}
                        />
                        {canEditFax && pharmacy.id && <PharmacyFaxEditor pharmacy={pharmacy} />}
                      </div>
                    }
                  />
                );
              })}
            </List>
          )}
        </div>
      </div>
    </>
  );
};

PharmacySearch.propTypes = {
  onSelect: PropTypes.func,
  initialPharmacies: PropTypes.array,
  defaultPharmacyId: PropTypes.string,
};

export default PharmacySearch;
